<template>
  <van-popup
    v-if="show"
    :value="show"
    get-container="body"
    :overlay="false"
    :lock-scroll="false"
    :close-on-click-overlay="false"
    :z-index="dialogZIndex"
    class="file-preview-overlay"
  >
    <!-- 文件预览 -->
    <div
      ref="previewDialog"
      class="file-preview-container"
      :class="{
        'is-dragging': dialogDragging,
        'is-resizing': dialogResizing,
        'is-pdf': isCurrentFilePdf,
      }"
      :style="dialogPositionStyle"
      role="dialog"
      :aria-label="$t('filePreview.dialog')"
      @pointerdown="startDialogDrag"
      @pointermove="moveDialog"
      @pointerup="stopDialogDrag"
      @pointercancel="stopDialogDrag"
      @lostpointercapture="stopDialogDrag"
    >
      <header class="file-preview-header">
        <div class="file-preview-header-info">
          <span class="file-preview-filename" :title="currentFileName">
            {{ currentFileName }}
          </span>
          <span v-if="files.length > 1 || !(mobilePdfViewer && isCurrentFilePdf)" class="file-preview-counter">
            {{ files.length ? currentIndex + 1 : 0 }} / {{ files.length }}
          </span>
        </div>
        <!-- 预览交互模式 -->
        <div
          v-if="readableUrl && !(mobilePdfViewer && isCurrentFilePdf)"
          class="file-preview-interaction-mode"
          role="group"
          :aria-label="$t('filePreview.interactionMode')"
          @pointerdown.stop
          @click.stop
        >
          <!-- {mode}模式下可使用文件自身的预览功能 -->
          <button
            type="button"
            :class="{ active: contentInteractionEnabled }"
            :aria-pressed="contentInteractionEnabled"
            :title="
              $t('filePreview.contentModeHint', { mode: currentOperationLabel })
            "
            @click="setContentInteraction(true)"
          >
            {{ currentOperationLabel }}
          </button>
          <!-- 在内容区域按住鼠标可拖动整个预览窗口 -->
          <!-- 全局拖拽 -->
          <button
            type="button"
            :class="{ active: !contentInteractionEnabled }"
            :aria-pressed="!contentInteractionEnabled"
            :title="$t('filePreview.globalDragHint')"
            @click="setContentInteraction(false)"
          >
            {{ $t("filePreview.globalDrag") }}
          </button>
        </div>
        <!-- 关闭文件预览 -->
        <button
          type="button"
          class="file-preview-close"
          :aria-label="$t('filePreview.close')"
          @pointerdown.stop
          @click="close"
        >
          <van-icon name="cross" />
        </button>
      </header>

      <!-- 上一个文件 -->
      <button
        v-if="files.length > 1"
        type="button"
        class="file-preview-prev"
        :class="{ disabled: currentIndex === 0 }"
        :aria-label="$t('filePreview.previous')"
        @pointerdown.stop
        @click="showPrevious"
      >
        <van-icon name="arrow-left" />
      </button>

      <!-- 下一个文件 -->
      <button
        v-if="files.length > 1"
        type="button"
        class="file-preview-next"
        :class="{ disabled: currentIndex === files.length - 1 }"
        :aria-label="$t('filePreview.next')"
        @pointerdown.stop
        @click="showNext"
      >
        <van-icon name="arrow" />
      </button>

      <div class="file-preview-content">
        <div class="file-preview-file">
          <div v-if="loading" class="file-preview-state">
            <van-loading size="28" color="#4286f5" />
            <!-- 文件加载中... -->
            <span>{{ $t("filePreview.loading") }}</span>
          </div>
          <div
            v-else-if="readableUrl && isCurrentFileImage"
            ref="mediaViewport"
            class="file-preview-image-viewport"
            :class="{
              'is-dragging': mediaDragging,
              'is-zoomed': mediaScale !== 1,
            }"
            @wheel.prevent="handleMediaWheel"
            @pointerdown="startMediaDrag"
            @pointermove="moveMedia"
            @pointerup="stopMediaDrag"
            @pointercancel="stopMediaDrag"
            @lostpointercapture="stopMediaDrag"
          >
            <img
              :src="readableUrl"
              :alt="currentFileName"
              class="file-preview-image"
              :style="mediaTransformStyle"
              draggable="false"
              @dragstart.prevent
              @load="handleImageLoaded"
            />
          </div>
          <div
            v-else-if="readableUrl && isCurrentFileVideo"
            ref="mediaViewport"
            class="file-preview-video-viewport"
            :class="{
              'is-dragging': mediaDragging,
              'is-zoomed': mediaScale !== 1,
            }"
            @wheel.prevent="handleMediaWheel"
          >
            <video
              v-show="!videoError"
              ref="videoPlayer"
              :src="readableUrl"
              :title="currentFileName"
              class="file-preview-video"
              :style="mediaTransformStyle"
              controls
              playsinline
              preload="metadata"
              @loadedmetadata="handleVideoLoaded"
              @canplay="handleVideoLoaded"
              @error="handleVideoError"
              @pointerdown="startVideoDrag"
              @pointermove="moveMedia"
              @pointerup="stopMediaDrag"
              @pointercancel="stopMediaDrag"
              @lostpointercapture="stopMediaDrag"
            ></video>
            <div
              v-if="videoLoading && !videoError"
              class="file-preview-state file-preview-video-loading"
            >
              <van-loading size="28" color="#4286f5" />
              <!-- 视频加载中... -->
              <span>{{ $t("filePreview.videoLoading") }}</span>
            </div>
            <div
              v-if="videoError"
              class="file-preview-state file-preview-video-error"
            >
              <van-icon name="warning-o" size="28" />
              <!-- 当前浏览器不支持该视频格式或编码 -->
              <span>{{ $t("filePreview.videoUnsupported") }}</span>
              <div class="file-preview-fallback-actions">
                <!-- 下载视频 -->
                <button
                  type="button"
                  @pointerdown.stop
                  @click.stop="downloadCurrentFile"
                >
                  {{ $t("filePreview.downloadVideo") }}
                </button>
                <!-- 新窗口打开 -->
                <button
                  type="button"
                  @pointerdown.stop
                  @click.stop="openCurrentFile"
                >
                  {{ $t("filePreview.openNewWindow") }}
                </button>
              </div>
            </div>
          </div>
          <MobilePdfViewer
            v-else-if="readableUrl && isCurrentFilePdf && mobilePdfViewer"
            :url="readableUrl"
            @download="$emit('download')"
          />
          <iframe
            v-else-if="readableUrl"
            :src="documentPreviewUrl"
            class="file-preview-iframe"
            :title="currentFileName"
            @load="handleDocumentLoaded"
          ></iframe>
          <div v-else class="file-preview-state">
            <van-icon name="warning-o" size="28" />
            <span>{{ displayedErrorText }}</span>
          </div>
          <!-- 拖动以移动预览窗口 -->
          <div
            v-if="readableUrl && !contentInteractionEnabled"
            class="file-preview-global-drag-layer"
            :title="$t('filePreview.dragToMove')"
          >
            <!-- 全局拖拽 -->
            <span>{{ $t("filePreview.globalDrag") }}</span>
          </div>
          <!-- 视频预览工具 -->
          <!-- 图片预览工具 -->
          <div
            v-if="
              readableUrl &&
              isCurrentFileTransformable &&
              !loading &&
              !videoError
            "
            class="file-preview-toolbar"
            :class="{ 'is-video': isCurrentFileVideo }"
            role="toolbar"
            :aria-label="
              $t(
                isCurrentFileVideo
                  ? 'filePreview.videoToolbar'
                  : 'filePreview.imageToolbar',
              )
            "
            @pointerdown.stop
            @click.stop
          >
            <!-- 向左旋转 -->
            <button
              v-if="isCurrentFileImage"
              type="button"
              :aria-label="$t('filePreview.rotateLeft')"
              @click="rotateLeft"
            >
              <van-icon name="replay" class="file-preview-rotate-left" />
            </button>
            <!-- 缩小视频 -->
            <!-- 缩小图片 -->
            <button
              type="button"
              :aria-label="
                $t(
                  isCurrentFileVideo
                    ? 'filePreview.zoomOutVideo'
                    : 'filePreview.zoomOutImage',
                )
              "
              @click="zoomOut"
            >
              <van-icon name="minus" />
            </button>
            <!-- 复位视频 -->
            <!-- 复位图片 -->
            <button
              type="button"
              class="file-preview-reset"
              :aria-label="
                $t(
                  isCurrentFileVideo
                    ? 'filePreview.resetVideo'
                    : 'filePreview.resetImage',
                )
              "
              @click="resetMediaTransform"
            >
              {{ zoomPercentage }}
            </button>
            <!-- 放大视频 -->
            <!-- 放大图片 -->
            <button
              type="button"
              :aria-label="
                $t(
                  isCurrentFileVideo
                    ? 'filePreview.zoomInVideo'
                    : 'filePreview.zoomInImage',
                )
              "
              @click="zoomIn"
            >
              <van-icon name="plus" />
            </button>
            <!-- 向右旋转 -->
            <button
              v-if="isCurrentFileImage"
              type="button"
              :aria-label="$t('filePreview.rotateRight')"
              @click="rotateRight"
            >
              <van-icon name="replay" />
            </button>
          </div>
        </div>
      </div>

      <span
        v-for="direction in dialogResizeDirections"
        :key="direction"
        class="file-preview-resize-handle"
        :class="`is-${direction}`"
        aria-hidden="true"
        @pointerdown.stop="startDialogResize($event, direction)"
        @pointermove.stop="resizeDialog"
        @pointerup.stop="stopDialogResize"
        @pointercancel.stop="stopDialogResize"
        @lostpointercapture.stop="stopDialogResize"
      ></span>
    </div>
  </van-popup>
</template>

<script>
import { getOssReadableUrl } from "@/mixins/uploadOss";
import {
  PREVIEW_ZOOM_FACTOR,
  getCenteredPreviewPosition,
  normalizePreviewRotation,
  zoomPreviewAroundPoint,
} from "@/utils/filePreviewTransform";
import { isVideoPreviewFile } from "@/utils/filePreviewType";
import { nextPopupZIndex } from "@/utils/popupLayer";
import { getPdfPageWidth } from "@/utils/documentCover";
import MobilePdfViewer from "@/components/MobilePdfViewer.vue";

const IMAGE_EXTENSIONS = ["gif", "jpg", "jpeg", "png", "webp", "bmp", "svg", "avif"];
const VIDEO_CONTROL_SAFE_AREA = 56;
const MIN_DIALOG_WIDTH = 320;
const MIN_DIALOG_HEIGHT = 240;

export default {
  name: "FilePreviewDialog",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    mobilePdfViewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogZIndex: 9999,
      currentIndex: 0,
      readableUrl: "",
      loading: false,
      errorText: "",
      errorKey: "",
      requestId: 0,
      dialogPosition: { x: 0, y: 0 },
      dialogSize: { width: 0, height: 0 },
      dialogDragging: false,
      dialogPointerId: null,
      dialogDragStart: null,
      dialogResizing: false,
      dialogResizePointerId: null,
      dialogResizeStart: null,
      dialogResizeDirections: [
        "north-west",
        "north-east",
        "south-west",
        "south-east",
      ],
      contentInteractionEnabled: true,
      mediaScale: 1,
      mediaRotation: 0,
      mediaOffsetX: 0,
      mediaOffsetY: 0,
      mediaDragging: false,
      mediaPointerId: null,
      mediaDragStart: null,
      videoLoading: false,
      videoError: false,
    };
  },
  computed: {
    currentFile() {
      return this.files[this.currentIndex] || null;
    },
    currentFileName() {
      const file = this.currentFile;
      if (file && typeof file === "object") {
        const originalName = file.originalName || file.name || file.fileName;
        if (originalName) return originalName;
      }
      const source = this.getFileSource(file);
      // 未知文件
      if (!source) return this.$t("filePreview.unknownFile");
      const cleanSource = source.split(/[?#]/)[0];
      const name = cleanSource.substring(cleanSource.lastIndexOf("/") + 1);
      // 未知文件
      if (!name) return this.$t("filePreview.unknownFile");
      try {
        return decodeURIComponent(name);
      } catch (error) {
        return name;
      }
    },
    isCurrentFileImage() {
      const file = this.currentFile;
      if (/^image\//i.test(file?.type || file?.mimeType || "")) return true;
      if (/^data:image\//i.test(this.getFileSource(file))) return true;
      return [this.currentFileName, this.getFileSource(this.currentFile)].some(
        (value) => IMAGE_EXTENSIONS.includes(this.getFileExtension(value)),
      );
    },
    isCurrentFileVideo() {
      const file = this.currentFile;
      return isVideoPreviewFile(file, {
        fileName: this.currentFileName,
        source: this.getFileSource(file),
      });
    },
    isCurrentFilePdf() {
      return [this.currentFileName, this.getFileSource(this.currentFile)].some(
        (value) => this.getFileExtension(value) === "pdf",
      );
    },
    documentPreviewUrl() {
      if (!this.readableUrl || !this.isCurrentFilePdf) return this.readableUrl;
      const [source, hash = ""] = this.readableUrl.split("#");
      const params = new URLSearchParams(hash);
      params.delete("view");
      params.set("zoom", "100");
      return `${source}#${params.toString()}`;
    },
    isCurrentFileTransformable() {
      return this.isCurrentFileImage || this.isCurrentFileVideo;
    },
    dialogPositionStyle() {
      const style = {
        left: `${this.dialogPosition.x}px`,
        top: `${this.dialogPosition.y}px`,
      };
      if (this.dialogSize.width > 0) {
        style.width = `${this.dialogSize.width}px`;
      }
      if (this.dialogSize.height > 0) {
        style.height = `${this.dialogSize.height}px`;
      }
      return style;
    },
    mediaTransformStyle() {
      const rotation = this.isCurrentFileImage ? this.mediaRotation : 0;
      return {
        transform: `translate3d(${this.mediaOffsetX}px, ${this.mediaOffsetY}px, 0) rotate(${rotation}deg) scale(${this.mediaScale})`,
      };
    },
    zoomPercentage() {
      const percentage = this.mediaScale * 100;
      if (percentage < 1000000) return `${Math.round(percentage)}%`;
      return `${percentage.toExponential(1)}%`;
    },
    currentOperationLabel() {
      // 图片操作
      if (this.isCurrentFileImage) return this.$t("filePreview.imageMode");
      // 视频操作
      if (this.isCurrentFileVideo) return this.$t("filePreview.videoMode");
      // 文档操作
      return this.$t("filePreview.documentMode");
    },
    displayedErrorText() {
      // 动态简中候选：由 this.errorKey 的本文件映射／赋值选择，保留原状态和键
      // filePreview.noFile：暂无可预览文件
      // filePreview.addressFailed：文件访问地址获取失败
      // filePreview.loadFailed：文件加载失败，请关闭后重试
      if (this.errorKey) return this.$t(this.errorKey);
      // 文件加载失败，请关闭后重试
      return this.errorText || this.$t("filePreview.loadFailed");
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.dialogZIndex = nextPopupZIndex(9999);
          this.open();
        } else {
          this.stopDialogDrag();
          this.stopDialogResize();
          this.pauseCurrentVideo();
          this.resetMediaTransform();
          this.cancelPendingRequest();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopDialogResize();
    this.pauseCurrentVideo();
    this.cancelPendingRequest();
  },
  methods: {
    getFileSource(file) {
      if (!file) return "";
      if (typeof file === "string") return file;
      return file.originUrl || file.url || "";
    },
    getFileExtension(filename) {
      if (!filename) return "";
      const cleanName = String(filename).split(/[?#]/)[0];
      const parts = cleanName.split(".");
      return parts.length > 1 ? parts.pop().toLowerCase() : "";
    },
    normalizeIndex(index) {
      if (!this.files.length) return 0;
      return Math.min(Math.max(Number(index) || 0, 0), this.files.length - 1);
    },
    async open() {
      this.currentIndex = this.normalizeIndex(this.initialIndex);
      this.stopDialogDrag();
      this.stopDialogResize();
      this.resetMediaTransform();
      this.contentInteractionEnabled = true;
      this.dialogSize = { width: 0, height: 0 };
      await this.$nextTick();
      if (!this.show) return;
      this.initializeDialogGeometry();
      await this.loadCurrentFile();
    },
    async loadCurrentFile() {
      if (this.pdfWidthController) this.pdfWidthController.abort();
      this.pauseCurrentVideo();
      this.resetMediaTransform();
      const file = this.currentFile;
      const source = this.getFileSource(file);
      const requestId = this.requestId + 1;
      this.requestId = requestId;
      this.readableUrl = "";
      this.errorText = "";
      this.errorKey = "";
      this.loading = false;
      this.videoLoading = false;
      this.videoError = false;
      this.contentInteractionEnabled = true;
      if (!source) {
        this.errorKey = "filePreview.noFile";
        return;
      }

      this.loading = true;
      try {
        const readableUrl = await getOssReadableUrl(source);
        if (!readableUrl) {
          this.errorKey = "filePreview.addressFailed";
          throw new Error("文件访问地址获取失败");
        }
        if (requestId !== this.requestId || !this.show) return;
        this.readableUrl = readableUrl;
        if (this.isCurrentFilePdf && !this.mobilePdfViewer) {
          this.fitPdfPageWidth(readableUrl, requestId);
        }
        this.videoLoading = this.isCurrentFileVideo;
        this.$emit("resolved", {
          file,
          index: this.currentIndex,
          url: readableUrl,
        });
      } catch (error) {
        if (requestId !== this.requestId || !this.show) return;
        this.errorText = error.message || "文件加载失败，请关闭后重试";
        if (!error.message && !this.errorKey) {
          this.errorKey = "filePreview.loadFailed";
        }
        this.$emit("error", { file, index: this.currentIndex, error });
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    cancelPendingRequest() {
      if (this.pdfWidthController) this.pdfWidthController.abort();
      this.requestId += 1;
      this.loading = false;
      this.videoLoading = false;
    },
    close() {
      this.stopDialogDrag();
      this.stopDialogResize();
      this.pauseCurrentVideo();
      this.resetMediaTransform();
      this.cancelPendingRequest();
      this.$emit("update:show", false);
      this.$emit("close");
    },
    async showPrevious() {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      await this.loadCurrentFile();
    },
    async showNext() {
      if (this.currentIndex >= this.files.length - 1) return;
      this.currentIndex += 1;
      await this.loadCurrentFile();
    },
    getPreviewDimensions() {
      const dialog = this.$refs.previewDialog;
      const rect = dialog && dialog.getBoundingClientRect();
      return {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        dialogWidth: rect ? rect.width : window.innerWidth * 0.8,
        dialogHeight: rect ? rect.height : window.innerHeight * 0.8,
      };
    },
    async fitPdfPageWidth(url, requestId) {
      const controller = new AbortController();
      this.pdfWidthController = controller;
      try {
        const pageWidth = await getPdfPageWidth(url, controller.signal);
        if (controller.signal.aborted || requestId !== this.requestId || !this.show) return;
        // 留出阅读器自身的页边距和滚动条。
        this.fitContentWidth(pageWidth + 20);
      } catch (error) {
        // 尺寸读取失败仍使用默认窗口，保留原生阅读器的预览能力。
      }
    },
    fitContentWidth(contentWidth, contentHeight = 0) {
      if (!Number.isFinite(contentWidth) || contentWidth <= 0) return;
      const viewport = this.$refs.previewDialog?.querySelector(".file-preview-file");
      const availableHeight = viewport?.clientHeight || this.dialogSize.height;
      const scale = contentHeight > 0
        ? Math.min(1, availableHeight / contentHeight)
        : 1;
      const width = Math.min(
        window.innerWidth - 24,
        Math.max(MIN_DIALOG_WIDTH, Math.ceil(contentWidth * scale)),
      );
      this.dialogPosition = {
        ...this.dialogPosition,
        x: Math.max(12, Math.min(window.innerWidth - width - 12,
          this.dialogPosition.x + (this.dialogSize.width - width) / 2)),
      };
      this.dialogSize = { ...this.dialogSize, width };
    },
    handleImageLoaded(event) {
      const image = event.currentTarget;
      this.fitContentWidth(image.naturalWidth, image.naturalHeight);
    },
    handleDocumentLoaded(event) {
      if (this.isCurrentFilePdf) return;
      try {
        const doc = event.currentTarget.contentDocument;
        const content = doc?.body?.firstElementChild;
        if (content) this.fitContentWidth(content.scrollWidth);
      } catch (error) {
        // 跨域阅读器不开放文档尺寸，继续使用紧凑的默认宽度。
      }
    },
    initializeDialogGeometry() {
      const dimensions = this.getPreviewDimensions();
      this.dialogSize = {
        width: dimensions.dialogWidth,
        height: dimensions.dialogHeight,
      };
      this.dialogPosition = getCenteredPreviewPosition(dimensions);
    },
    startDialogDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (this.mediaDragging || this.isDialogDragInteractiveArea(event)) {
        return;
      }
      this.stopMediaDrag();
      this.dialogDragging = true;
      this.dialogPointerId = event.pointerId;
      this.dialogDragStart = {
        clientX: event.clientX,
        clientY: event.clientY,
        x: this.dialogPosition.x,
        y: this.dialogPosition.y,
      };
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
    },
    isDialogDragInteractiveArea(event) {
      const target = event.target;
      if (!target || !target.closest) return true;
      const isHeader = target.closest(".file-preview-header");
      const isGlobalDragLayer = target.closest(
        ".file-preview-global-drag-layer",
      );
      if (!isHeader && !isGlobalDragLayer) return true;
      return Boolean(
        target.closest("button, a, input, select, textarea, [role='button']"),
      );
    },
    moveDialog(event) {
      if (
        !this.dialogDragging ||
        event.pointerId !== this.dialogPointerId ||
        !this.dialogDragStart
      ) {
        return;
      }
      // 弹窗位置不做视口边界钳制，允许按用户需要拖动到任意坐标。
      this.dialogPosition = {
        x:
          this.dialogDragStart.x + event.clientX - this.dialogDragStart.clientX,
        y:
          this.dialogDragStart.y + event.clientY - this.dialogDragStart.clientY,
      };
    },
    stopDialogDrag(event) {
      if (
        event &&
        this.dialogPointerId !== null &&
        event.pointerId !== this.dialogPointerId
      ) {
        return;
      }
      if (
        event &&
        event.currentTarget &&
        event.currentTarget.hasPointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      this.dialogDragging = false;
      this.dialogPointerId = null;
      this.dialogDragStart = null;
    },
    startDialogResize(event, direction) {
      if (event.button !== undefined && event.button !== 0) return;
      const dialog = this.$refs.previewDialog;
      if (!dialog) return;
      const rect = dialog.getBoundingClientRect();
      this.stopDialogDrag();
      this.stopMediaDrag();
      this.dialogResizing = true;
      this.dialogResizePointerId = event.pointerId;
      this.dialogResizeStart = {
        direction,
        clientX: event.clientX,
        clientY: event.clientY,
        x: this.dialogPosition.x,
        y: this.dialogPosition.y,
        width: rect.width,
        height: rect.height,
      };
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
    },
    resizeDialog(event) {
      if (
        !this.dialogResizing ||
        event.pointerId !== this.dialogResizePointerId ||
        !this.dialogResizeStart
      ) {
        return;
      }
      const start = this.dialogResizeStart;
      const deltaX = event.clientX - start.clientX;
      const deltaY = event.clientY - start.clientY;
      const resizeFromWest = start.direction.includes("west");
      const resizeFromNorth = start.direction.includes("north");
      const requestedWidth = start.width + (resizeFromWest ? -deltaX : deltaX);
      const requestedHeight =
        start.height + (resizeFromNorth ? -deltaY : deltaY);
      const width = Math.max(MIN_DIALOG_WIDTH, requestedWidth);
      const height = Math.max(MIN_DIALOG_HEIGHT, requestedHeight);

      this.dialogSize = { width, height };
      this.dialogPosition = {
        x: resizeFromWest ? start.x + start.width - width : start.x,
        y: resizeFromNorth ? start.y + start.height - height : start.y,
      };
      event.preventDefault();
    },
    stopDialogResize(event) {
      if (
        event &&
        this.dialogResizePointerId !== null &&
        event.pointerId !== this.dialogResizePointerId
      ) {
        return;
      }
      if (
        event &&
        event.currentTarget &&
        event.currentTarget.hasPointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      this.dialogResizing = false;
      this.dialogResizePointerId = null;
      this.dialogResizeStart = null;
    },
    setContentInteraction(enabled) {
      this.stopDialogDrag();
      this.stopMediaDrag();
      this.contentInteractionEnabled = enabled;
    },
    resetMediaTransform() {
      this.stopMediaDrag();
      this.mediaScale = 1;
      this.mediaRotation = 0;
      this.mediaOffsetX = 0;
      this.mediaOffsetY = 0;
    },
    applyMediaZoom(factor, anchorX = 0, anchorY = 0) {
      const transform = zoomPreviewAroundPoint(
        {
          scale: this.mediaScale,
          offsetX: this.mediaOffsetX,
          offsetY: this.mediaOffsetY,
        },
        { factor, anchorX, anchorY },
      );
      this.mediaScale = transform.scale;
      this.mediaOffsetX = transform.offsetX;
      this.mediaOffsetY = transform.offsetY;
    },
    handleMediaWheel(event) {
      if (!event.deltaY) return;
      const viewport = this.$refs.mediaViewport;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      this.applyMediaZoom(
        event.deltaY < 0 ? PREVIEW_ZOOM_FACTOR : 1 / PREVIEW_ZOOM_FACTOR,
        event.clientX - rect.left - rect.width / 2,
        event.clientY - rect.top - rect.height / 2,
      );
    },
    zoomIn() {
      this.applyMediaZoom(PREVIEW_ZOOM_FACTOR);
    },
    zoomOut() {
      this.applyMediaZoom(1 / PREVIEW_ZOOM_FACTOR);
    },
    rotateLeft() {
      this.mediaRotation = normalizePreviewRotation(this.mediaRotation - 90);
    },
    rotateRight() {
      this.mediaRotation = normalizePreviewRotation(this.mediaRotation + 90);
    },
    startMediaDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (this.mediaScale === 1) return;
      event.stopPropagation();
      this.beginMediaDrag(event, true);
    },
    startVideoDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (this.mediaScale === 1 || this.isVideoControlArea(event)) return;
      event.stopPropagation();
      this.beginMediaDrag(event, false);
    },
    isVideoControlArea(event) {
      const video = this.$refs.videoPlayer;
      if (!video) return false;
      const rect = video.getBoundingClientRect();
      const safeArea = Math.min(VIDEO_CONTROL_SAFE_AREA, rect.height / 3);
      return event.clientY >= rect.bottom - safeArea;
    },
    beginMediaDrag(event, preventDefault) {
      this.mediaDragging = true;
      this.mediaPointerId = event.pointerId;
      this.mediaDragStart = {
        clientX: event.clientX,
        clientY: event.clientY,
        offsetX: this.mediaOffsetX,
        offsetY: this.mediaOffsetY,
      };
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      if (preventDefault) event.preventDefault();
    },
    moveMedia(event) {
      if (
        !this.mediaDragging ||
        event.pointerId !== this.mediaPointerId ||
        !this.mediaDragStart
      ) {
        return;
      }
      this.mediaOffsetX =
        this.mediaDragStart.offsetX +
        event.clientX -
        this.mediaDragStart.clientX;
      this.mediaOffsetY =
        this.mediaDragStart.offsetY +
        event.clientY -
        this.mediaDragStart.clientY;
      event.preventDefault();
    },
    stopMediaDrag(event) {
      if (
        event &&
        this.mediaPointerId !== null &&
        event.pointerId !== this.mediaPointerId
      ) {
        return;
      }
      if (
        event &&
        event.currentTarget &&
        event.currentTarget.hasPointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      this.mediaDragging = false;
      this.mediaPointerId = null;
      this.mediaDragStart = null;
    },
    handleVideoLoaded(event) {
      if (event.type === "loadedmetadata") {
        const video = event.currentTarget;
        this.fitContentWidth(video.videoWidth, video.videoHeight);
      }
      this.videoLoading = false;
      this.videoError = false;
    },
    handleVideoError() {
      this.videoLoading = false;
      this.videoError = true;
      const error = new Error("当前浏览器不支持该视频格式或编码");
      this.$emit("error", {
        file: this.currentFile,
        index: this.currentIndex,
        error,
      });
    },
    pauseCurrentVideo() {
      const video = this.$refs.videoPlayer;
      if (video && typeof video.pause === "function") video.pause();
    },
    downloadCurrentFile() {
      if (!this.readableUrl) return;
      const link = document.createElement("a");
      link.href = this.readableUrl;
      link.download = this.currentFileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openCurrentFile() {
      if (!this.readableUrl) return;
      const openedWindow = window.open(
        this.readableUrl,
        "_blank",
        "noopener,noreferrer",
      );
      if (openedWindow) openedWindow.opener = null;
    },
  },
};
</script>

<style lang="less" scoped>
.file-preview-overlay {
  position: fixed;
  inset: 0;
  width: auto;
  height: auto;
  max-height: none;
  overflow: visible;
  background: transparent;
  pointer-events: none;
  transform: none;

  .file-preview-container {
    position: fixed;
    display: flex;
    width: min(80vw, 900px);
    height: 80vh;
    overflow: hidden;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
    flex-direction: column;
    cursor: default;
    pointer-events: auto;

    &.is-pdf {
      width: min(80vw, 900px);
    }

    &.is-dragging {
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.34);
      cursor: grabbing;

      .file-preview-global-drag-layer {
        cursor: grabbing;
      }
    }

    &.is-resizing {
      user-select: none;
    }

    .file-preview-resize-handle {
      position: absolute;
      z-index: 30;
      width: 18px;
      height: 18px;
      touch-action: none;

      &::after {
        position: absolute;
        width: 8px;
        height: 8px;
        border-color: rgba(66, 134, 245, 0.72);
        border-style: solid;
        content: "";
        opacity: 0.45;
        transition: opacity 0.2s ease;
      }

      &:hover::after {
        opacity: 1;
      }

      &.is-north-west {
        top: 0;
        left: 0;
        cursor: nwse-resize;

        &::after {
          top: 3px;
          left: 3px;
          border-width: 2px 0 0 2px;
        }
      }

      &.is-north-east {
        top: 0;
        right: 0;
        cursor: nesw-resize;

        &::after {
          top: 3px;
          right: 3px;
          border-width: 2px 2px 0 0;
        }
      }

      &.is-south-west {
        bottom: 0;
        left: 0;
        cursor: nesw-resize;

        &::after {
          bottom: 3px;
          left: 3px;
          border-width: 0 0 2px 2px;
        }
      }

      &.is-south-east {
        right: 0;
        bottom: 0;
        cursor: nwse-resize;

        &::after {
          right: 3px;
          bottom: 3px;
          border-width: 0 2px 2px 0;
        }
      }
    }
  }

  .file-preview-header {
    display: flex;
    height: 48px;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 10px 0 16px;
    border-bottom: 1px solid #e8e8e8;
    background: #fff;
    cursor: move;
    cursor: grab;
    touch-action: none;
    user-select: none;

    &:active {
      cursor: grabbing;
    }
  }

  .file-preview-header-info {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 12px;
  }

  .file-preview-close {
    display: flex;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    color: #606266;
    background: transparent;
    font-size: 18px;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: #f2f3f5;
    }
  }

  .file-preview-interaction-mode {
    display: flex;
    height: 30px;
    flex-shrink: 0;
    align-items: center;
    padding: 2px;
    border: 1px solid #d8e5f8;
    border-radius: 6px;
    background: #f4f8ff;
    font-size: 12px;

    button {
      height: 24px;
      padding: 0 9px;
      border: 0;
      border-radius: 4px;
      color: #526173;
      background: transparent;
      font-size: 12px;
      white-space: nowrap;
      cursor: pointer;
      transition: color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

      &:hover:not(.active) {
        color: #4286f5;
        background: #eaf2ff;
      }

      &.active {
        color: #fff;
        background: #4286f5;
        box-shadow: 0 1px 4px rgba(66, 134, 245, 0.3);
        cursor: default;
      }
    }
  }

  .file-preview-prev,
  .file-preview-next {
    position: absolute;
    z-index: 10;
    top: calc(50% + 24px);
    display: flex;
    width: 50px;
    height: 50px;
    align-items: center;
    justify-content: center;
    transform: translateY(-50%);
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: rgba(0, 0, 0, 0.5);
    font-size: 24px;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
  }

  .file-preview-prev {
    left: 16px;
  }

  .file-preview-next {
    right: 16px;
  }

  .file-preview-content {
    display: flex;
    width: 100%;
    min-height: 0;
    flex: 1;
    flex-direction: column;
  }

  .file-preview-file {
    position: relative;
    display: flex;
    min-height: 0;
    flex: 1;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #f5f5f5;

    .file-preview-global-drag-layer {
      position: absolute;
      z-index: 6;
      inset: 0;
      cursor: grab;
      touch-action: none;
      user-select: none;

      span {
        position: absolute;
        right: 12px;
        bottom: 12px;
        padding: 5px 9px;
        border-radius: 12px;
        color: #fff;
        background: rgba(0, 0, 0, 0.5);
        font-size: 12px;
        line-height: 1;
        pointer-events: none;
      }
    }
  }

  .file-preview-image-viewport,
  .file-preview-video-viewport {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &.is-dragging {
      cursor: grabbing;
    }
  }

  .file-preview-image-viewport {
    touch-action: none;

    &.is-zoomed {
      cursor: grab;
    }
  }

  .file-preview-image {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
    transform-origin: center center;
    user-select: none;
    will-change: transform;
  }

  .file-preview-video-viewport {
    position: relative;
    background: #000;

    &.is-zoomed {
      .file-preview-video {
        cursor: grab;
      }
    }

    &.is-dragging {
      .file-preview-video {
        cursor: grabbing;
      }
    }
  }

  .file-preview-video {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transform-origin: center center;
    will-change: transform;
  }

  .file-preview-video-loading,
  .file-preview-video-error {
    position: absolute;
    z-index: 4;
    inset: 0;
    background: #f5f5f5;
  }

  .file-preview-video-loading {
    pointer-events: none;
  }

  .file-preview-fallback-actions {
    display: flex;
    align-items: center;
    gap: 10px;

    button {
      height: 34px;
      padding: 0 14px;
      border: 1px solid #4286f5;
      border-radius: 4px;
      color: #4286f5;
      background: #fff;
      cursor: pointer;

      &:hover {
        color: #fff;
        background: #4286f5;
      }
    }
  }

  .file-preview-iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }

  .file-preview-state {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    color: #909399;
    font-size: 14px;
    text-align: center;
  }

  .file-preview-toolbar {
    position: absolute;
    z-index: 12;
    bottom: 16px;
    left: 50%;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px;
    transform: translateX(-50%);
    border-radius: 22px;
    background: rgba(0, 0, 0, 0.68);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);

    &.is-video {
      bottom: 64px;
    }

    button {
      display: flex;
      width: 34px;
      height: 34px;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 0;
      border-radius: 17px;
      color: #fff;
      background: transparent;
      font-size: 17px;
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      &.file-preview-reset {
        width: auto;
        min-width: 58px;
        padding: 0 8px;
        font-size: 12px;
      }
    }
  }

  .file-preview-rotate-left {
    transform: scaleX(-1);
  }

  .file-preview-filename {
    display: block;
    min-width: 0;
    overflow: hidden;
    flex: 1;
    color: #333;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-preview-counter {
    flex-shrink: 0;
    color: #999;
    font-size: 14px;
  }
}

@media (max-width: 767px) {
  .file-preview-overlay {
    .file-preview-container,
    .file-preview-container.is-pdf {
      width: calc(100vw - 24px);
      max-width: calc(100vw - 24px);
      height: 90vh;
      height: 90dvh;
      max-height: 90vh;
      max-height: 90dvh;
    }
  }
}
</style>
